import {
  Component,
  HostListener,
  OnInit,
  ViewEncapsulation
} from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { skip } from 'rxjs/operators'

import { environment } from '../environments/environment'
import { appConstants } from './app.constants'
import { User } from './common/interfaces/user.interface'
import { AuthService } from './common/services/auth.service'
import { EventService } from './common/services/event.service'
import { NativescriptInterfaceService } from './common/services/nativescript-interface.service'
import { ViewportService } from './common/services/viewport.service'
import { ConnectivityService } from './common/services/offline/connectivity.service'
import { OfflineResourceService } from './common/services/offline/offline-resource.service'
import { SyncQueueService } from './common/services/offline/sync-queue.service'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./styles/main.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {
  currentUser: User
  path: string
  isLogin: boolean

  private eventSubscriptions = new Subscription()
  private subscription = new Subscription()

  isTouchResolution: boolean
  private cacheWarmedUp = false

  constructor(
    private router: Router,
    private eventService: EventService,
    private authService: AuthService,
    private viewportService: ViewportService,
    private nativescriptInterfaceService: NativescriptInterfaceService,
    private connectivityService: ConnectivityService,
    private offlineResourceService: OfflineResourceService,
    private syncQueueService: SyncQueueService
  ) {
    this.eventSubscriptions.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.eventService.routeChanged.next({ url: event.url })
        }
      })
    )
  }

  ngOnInit() {
    this.setIsTouchResolution()
    this.viewportService.isTouchResolution.subscribe(
      (isTouchResolution: boolean) => {
        this.isTouchResolution = isTouchResolution
      }
    )
    this.authService.currentUser.subscribe((userRes: User) => {
      this.currentUser = userRes

      // Warm up cache when user is logged in (embedded only)
      if (userRes && environment.nativescriptEmbedded && !this.cacheWarmedUp) {
        this.cacheWarmedUp = true
        this.offlineResourceService.warmUpCache().catch(err => {
          console.error('Cache warm-up error:', err)
        })
      }
    })
    this.subscription.add(
      this.eventService.routeChanged.subscribe(routeChanged => {
        // Scroll top
        window.scrollTo(0, 0)
        this.path = routeChanged.url.includes('?')
          ? routeChanged.url.substring(0, routeChanged.url.indexOf('?'))
          : routeChanged.url
        this.isLogin =
          this.path.includes('/login') ||
          this.path.includes('forgot-password') ||
          this.path.includes('reset-password')

        if (this.authService.check() && !this.currentUser) {
          this.authService.meWithCache().subscribe(
            (userRes: User) => {
              this.authService.currentUser.next(userRes)
            },
            err => {
              // Réseau ET cache échouent → logout
              this.router.navigate(['/logout'])
            }
          )
        }
      })
    )

    if (environment.nativescriptEmbedded) {
      this.nativescriptInterfaceService.initInterface()
    }

    // Restore pending sync counts from IndexedDB (persisted across app restarts)
    this.syncQueueService.loadPersistedCounts().catch(() => {})

    // Trigger sync queue + cache refresh when coming back online
    this.subscription.add(
      this.connectivityService.isOnline$.pipe(skip(1)).subscribe(online => {
        if (online) {
          this.syncQueueService.processQueue()
          this.offlineResourceService.warmUpCache().catch(() => {})

          if (this.currentUser) {
            this.authService.revalidate().subscribe(
              (userRes: User) => {
                this.authService.currentUser.next(userRes)
              },
              err => {
                // Rejet serveur (user désactivé, token révoqué) → logout
                if (err && err.status && err.status >= 400 && err.status < 500) {
                  this.router.navigate(['/logout'])
                }
                // Erreur réseau transitoire → on ignore, l'user continue
              }
            )
          }
        }
      })
    )
  }

  @HostListener('window:resize')
  onResize() {
    this.setIsTouchResolution()
  }

  setIsTouchResolution(): void {
    this.viewportService.isTouchResolution.next(
      window.innerWidth < appConstants.TOUCH_BREAKPOINT
    )
  }
}
